import { useState } from "react";
import styled from "@emotion/styled";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Divider,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import BackupIcon from "@mui/icons-material/Backup";
import SyncIcon from "@mui/icons-material/Sync";
import ContactsIcon from "@mui/icons-material/Contacts";
import useCsvParser from "../../../../hooks/useCsvParser";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import useSnackbarStore from "../../../../store/snackbar";

import { importData } from "../apiService";

const RESOURCES = {
  customers: {
    label: "Customers",
    requiredHeaders: ["first name", "last name", "mobile" ],
    optionalHeaders: [ "id", "email", "address", "city", "state", "zip"],
    notes: [
      { field: "mobile", note: "10-digit US format, e.g. 8455551234" },
      { field: "email", note: "Must be a valid email address" }
    ],
    syncOptions: [
      { key: "google", label: "Sync Google Contacts", icon: <ContactsIcon fontSize="small" /> },
      { key: "icloud", label: "Sync iCloud Contacts", icon: <SyncIcon fontSize="small" /> },
    ],
     headerMap: {
      "first name": "first_name",
      "last name": "last_name",
      "mobile": "mobile_phone",
      "id": "customer_number"
    },
  },
  // vendors: {
  //   label: "Vendors",
  //   requiredHeaders: ["name", "email", "phone", "address", "city", "state", "zip"],
  //   optionalHeaders: ["account_number", "payment_terms", "notes"],
  //   notes: [
  //     { field: "phone", note: "10-digit US format" },
  //     { field: "state", note: "2-letter abbreviation" },
  //     { field: "payment_terms", note: "e.g. Net 30, Net 60, COD, Due on Receipt" },
  //   ],
  // },
  products: {
    label: "Products",
    requiredHeaders: ["name", "price", "cost", "quantity"],
    optionalHeaders: ["description", "category", "brand", "size", "color", "low stock alert"],
    notes: [
      { field: "sku", note: "Unique identifier, alphanumeric, no spaces" },
      { field: "price", note: "Numeric, no currency symbols, e.g. 29.99" },
      { field: "cost", note: "Numeric, no currency symbols, e.g. 29.99" },
      { field: "quantity", note: "Whole number, current stock on hand" },
      { field: "weight", note: "In lbs, numeric only" },
      { field: "barcode", note: "UPC or EAN format" },
    ],
    headerMap: {
      "quantity": "quantity_in_stock",
      "low stock alert": "low_stock_threshold",
    },
  },
  // services: {
  //   label: "Services",
  //   requiredHeaders: ["name", "price", "duration_minutes"],
  //   optionalHeaders: ["description", "category", "taxable"],
  //   notes: [
  //     { field: "price", note: "Numeric, no currency symbols" },
  //     { field: "duration_minutes", note: "Whole number, e.g. 30, 60, 90" },
  //     { field: "taxable", note: "true or false" },
  //   ],
  // },
  // inventory: {
  //   label: "Inventory Adjustments",
  //   requiredHeaders: ["sku", "quantity", "adjustment_type", "reason"],
  //   optionalHeaders: ["notes", "date"],
  //   notes: [
  //     { field: "sku", note: "Must match an existing product SKU" },
  //     { field: "quantity", note: "Positive number (direction is set by adjustment_type)" },
  //     { field: "adjustment_type", note: "One of: add, remove, set" },
  //     { field: "date", note: "YYYY-MM-DD format, defaults to today if omitted" },
  //   ],
  // },
  // appointments: {
  //   label: "Appointments",
  //   requiredHeaders: ["customer_name", "service_name", "date", "time"],
  //   optionalHeaders: ["stylist", "notes", "status"],
  //   notes: [
  //     { field: "customer_name", note: "Must match an existing customer record" },
  //     { field: "service_name", note: "Must match an existing service" },
  //     { field: "date", note: "YYYY-MM-DD format" },
  //     { field: "time", note: "24h format, e.g. 14:30" },
  //     { field: "status", note: "One of: scheduled, confirmed, completed, cancelled" },
  //   ],
  // },
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;
`;

const TopSection = styled.div`
  flex-shrink: 0;
`;

const MiddleSection = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin-top: 8px;
`;

const BottomSection = styled.div`
  flex-shrink: 0;
  padding-top: 16px;
`;

const HeaderSection = styled.div`
  margin-bottom: 16px;
`;

const SectionLabel = styled(Typography)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const NotesList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  li {
    font-size: 13px;
    color: #555;
    margin-bottom: 4px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SyncRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
`;

const DividerLine = styled(Divider)`
  flex: 1;
`;

export default function CsvImport() {
  const [resource, setResource] = useState("");
  const config = resource ? RESOURCES[resource] : null;

  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const { file, parsedData, error, warnings, fileRef, handleFile, reset } =
    useCsvParser(config);

    console.log(parsedData, "parsedData");

  function handleResourceChange(e) {
    setResource(e.target.value);
    reset();
  }

  const { mutate } = useMutation({
    mutationFn: () => importData(resource, parsedData?.rows, parsedData?.headers),
    onSuccess: () => {
      showSnackbar("Data imported successfully.", "success");
      reset();
    },
    onError: () => {
      showSnackbar("Error importing data.", "error");
    },
  })

  function handleImport() {
    if (parsedData) {
      mutate();
    }
  }

  function handleSync(key) {
    // TODO: implement sync logic
    console.log(`Sync triggered: ${key}`);
  }

  return (
    <Wrapper>
      <TopSection>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Import and Sync Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a resource, review required headers, then upload your file.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2, bgcolor: "#fff"  }}>
          <InputLabel>Resource</InputLabel>
          <Select value={resource} label="Resource" onChange={handleResourceChange}>
            {Object.entries(RESOURCES).map(([key, val]) => (
              <MenuItem key={key} value={key}>
                {val.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TopSection>

      <MiddleSection>
        {config && (
          <>
            {/* Sync options (e.g. for Customers) */}
            {config.syncOptions && (
              <>
                <SyncRow>
                  {config.syncOptions.map((opt) => (
                    <Button
                      key={opt.key}
                      variant="outlined"
                      size="small"
                      startIcon={opt.icon}
                      onClick={() => handleSync(opt.key)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </SyncRow>
                <OrDivider>
                  <DividerLine />
                  <Typography variant="caption" color="text.secondary">
                    or import from CSV
                  </Typography>
                  <DividerLine />
                </OrDivider>
              </>
            )}

            {/* Headers */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <HeaderSection>
                <SectionLabel>Required Headers</SectionLabel>
                <ChipRow>
                  {config.requiredHeaders.map((h) => (
                    <Chip key={h} label={h} size="small" color="success" variant="outlined" />
                  ))}
                </ChipRow>
              </HeaderSection>
              {config.optionalHeaders?.length > 0 && (
                <HeaderSection>
                  <SectionLabel>Optional Headers</SectionLabel>
                  <ChipRow>
                    {config.optionalHeaders.map((h) => (
                      <Chip key={h} label={h} size="small" variant="outlined" />
                    ))}
                  </ChipRow>
                </HeaderSection>
              )}
              {config.notes?.length > 0 && (
                <div>
                  <SectionLabel>Field Notes</SectionLabel>
                  <NotesList>
                    {config.notes.map((n) => (
                      <li key={n.field}>
                        <strong>{n.field}</strong> — {n.note}
                      </li>
                    ))}
                  </NotesList>
                </div>
              )}
            </Paper>

            {/* Upload */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<FileUploadIcon />}
              size="small"
              sx={{ mb: 2 }}
            >
              {file ? file.name : "Choose CSV File"}
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={handleFile} />
            </Button>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Unrecognized columns (will be ignored): {warnings.join(", ")}
              </Alert>
            )}

            {/* Preview */}
            {parsedData && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>{parsedData.rows.length}</strong> row
                  {parsedData.rows.length !== 1 && "s"}
                  {" · "}
                  <strong>{parsedData.headers.length}</strong> column
                  {parsedData.headers.length !== 1 && "s"}
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {parsedData.headers.map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.rows.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {parsedData.mappedHeaders.map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                whiteSpace: "nowrap",
                                maxWidth: 200,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row[h]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {parsedData.rows.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Showing first 5 of {parsedData.rows.length} rows
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </MiddleSection>

      {/* Sticky import button at bottom */}
      {parsedData && (
        <BottomSection>
          <Button variant="contained" onClick={handleImport} disableElevation fullWidth startIcon={<BackupIcon />} size="large">
            Import {parsedData.rows.length} Row{parsedData.rows.length !== 1 && "s"} into{" "}
            {config?.label}
          </Button>
        </BottomSection>
      )}
    </Wrapper>
  );
}